import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Settings, Plus, ChevronUp, ChevronDown, Save } from "lucide-react";
import { getEmployerSettings, saveEmployerSettings } from "@/lib/api";
import { toast } from "sonner";

function OptionEditor({ title, hint, items, setItems, testid, addLabel }) {
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };
  const rename = (i, label) => setItems(items.map((it, k) => (k === i ? { ...it, label } : it)));
  const toggle = (i) => setItems(items.map((it, k) => (k === i ? { ...it, active: !it.active } : it)));
  const add = () => setItems([...items, { id: null, label: "", active: true }]);

  return (
    <div className="panel" data-testid={`${testid}-panel`} style={{ marginBottom: 18 }}>
      <div className="ph"><h2>{title}</h2></div>
      <div className="pb">
        <p className="mut" style={{ marginBottom: 12 }}>{hint}</p>
        <div className="setup-list" data-testid={`${testid}-list`}>
          {items.map((it, i) => (
            <div className={`setup-row${it.active ? "" : " inactive"}`} key={it.id || `new-${i}`} data-testid={`${testid}-row-${i}`}>
              <div className="reorder">
                <button className="setup-order-btn" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`${testid}-up-${i}`} aria-label="Move up"><ChevronUp size={15} /></button>
                <button className="setup-order-btn" onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`${testid}-down-${i}`} aria-label="Move down"><ChevronDown size={15} /></button>
              </div>
              <input className="setup-label" value={it.label} onChange={(e) => rename(i, e.target.value)} placeholder="Name…" data-testid={`${testid}-label-${i}`} />
              {!it.active && <span className="setup-inactive-tag" data-testid={`${testid}-inactive-${i}`}>Hidden</span>}
              <button className="setup-toggle" onClick={() => toggle(i)} data-testid={`${testid}-toggle-${i}`}>
                {it.active ? "Deactivate" : "Reactivate"}
              </button>
            </div>
          ))}
        </div>
        <button className="btn ghost" onClick={add} style={{ marginTop: 12 }} data-testid={`${testid}-add`}><Plus size={16} /> {addLabel}</button>
      </div>
    </div>
  );
}

export default function InterviewSetup() {
  const [positions, setPositions] = useState(null);
  const [supervisors, setSupervisors] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => getEmployerSettings().then((d) => { setPositions(d.positions); setSupervisors(d.supervisors); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!positions || !supervisors) return <div className="empty" data-testid="setup-loading">Loading interview setup…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const r = await saveEmployerSettings({ positions, supervisors });
      setPositions(r.positions); setSupervisors(r.supervisors);
      toast.success("Interview settings saved — changes appear in the employer handoff immediately");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="interview-setup">
      <div className="crumb"><Link to="/dashboard">Staffing Home</Link> › <strong>Interview Setup</strong></div>

      <div className="panel" style={{ marginBottom: 18 }}>
        <div className="ph"><Settings size={18} style={{ opacity: .7 }} /><h2>Employer View Settings · Hope's Cookies</h2></div>
        <div className="pb">
          <p className="mut">Manage the Positions and Interviewers shown in the Employer Interview Handoff dropdowns. Changes take effect immediately — no development needed. Deactivating an option hides it from future selections while preserving its value on existing candidate records.</p>
        </div>
      </div>

      <OptionEditor
        title="Positions"
        hint="Reorder, rename, add, or deactivate the positions employers can assign at hire."
        items={positions}
        setItems={setPositions}
        testid="positions"
        addLabel="Add position"
      />

      <OptionEditor
        title="Interviewers / Supervisors"
        hint="Reorder, rename, add, or deactivate who can be recorded as completing a handoff or as an assigned supervisor."
        items={supervisors}
        setItems={setSupervisors}
        testid="supervisors"
        addLabel="Add interviewer"
      />

      <div className="action-row" style={{ position: "sticky", bottom: 0, background: "var(--bg)", padding: "12px 0" }}>
        <button className="btn primary" onClick={save} disabled={saving} data-testid="save-settings"><Save size={16} /> {saving ? "Saving…" : "Save settings"}</button>
        <button className="btn ghost" onClick={load} disabled={saving} data-testid="reset-settings">Discard changes</button>
      </div>
    </div>
  );
}
